import { FC, useMemo } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'

import { useNavigate, useParams } from 'react-router-dom'
import { useOpenOffersList, useOpenTermsOfUse } from 'services/openOffers/openOffers.hooks'
import { OfferCard } from 'components/Offer'
import OpenOffersLayout from 'layouts/OpenOffers'
import { useIntl } from 'react-intl'
import TermsOfUseForm from 'components/TermsOfUse/TermsOfUseForm'

const OpenOffersPage: FC = () => {
  const { formatMessage } = useIntl()
  const { token } = useParams()
  const {
    hasUserConsented,
    isLoadingTermsOfUse,
    termsOfUse,
    isLoading: isTermsOfUseLoading,
    mutateAsyncTermsOfUse,
    refetch: refetchTermsOfUse,
    isFetched
  } = useOpenTermsOfUse(token as string)
  const { data: offerList = [], isLoading: isOffersListLoading } = useOpenOffersList(
    token as string
  )

  const navigate = useNavigate()

  const isLoading = useMemo(
    () => (isOffersListLoading || isLoadingTermsOfUse) && !isFetched,
    [isOffersListLoading, isLoadingTermsOfUse, isFetched]
  )

  if (isLoading) {
    return (
      <OpenOffersLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </OpenOffersLayout>
    )
  }

  if (!hasUserConsented) {
    return (
      <OpenOffersLayout>
        <TermsOfUseForm
          isLoading={isTermsOfUseLoading}
          onAccept={async () => {
            await mutateAsyncTermsOfUse(termsOfUse?.id)
            await refetchTermsOfUse()
          }}
          onExit={() => navigate('/')}
          termsOfUse={termsOfUse}
        />
      </OpenOffersLayout>
    )
  }

  if (offerList.length <= 0) {
    return (
      <OpenOffersLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant='h5' fontWeight={500}>
            {formatMessage({
              id: 'widgetOffer.noActiveOffers',
              defaultMessage: 'Nie masz aktywnych ofert, poproś o nową ofertę'
            })}
          </Typography>
        </Box>
      </OpenOffersLayout>
    )
  }

  return (
    <OpenOffersLayout>
      {offerList.map((offer) => (
        <OfferCard
          key={`offer-${offer.id}`}
          offer={offer}
          onSeeOffer={() => navigate(`/oferta/${token}/${offer.id}`)}
        />
      ))}
    </OpenOffersLayout>
  )
}

export default OpenOffersPage
